/* Cost breakdown receipt formatting (28 Sep 2026). Money is SGD = USD × rate, shown as "$1.23". */
import type { CostLine } from "../store/api";

export function formatSgd(usd: number, rate: number): string {
  const sgd = (Number.isFinite(usd) ? usd : 0) * rate;
  if (sgd > 0 && sgd < 0.01) return "<$0.01";
  return `$${sgd.toLocaleString("en-SG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatTokens(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n < 1000) return String(Math.round(n));
  if (n < 999_500) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0).replace(/\.0$/, "")}k`;
  return `${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}M`;
}

export const tokenTotal = (c: CostLine) => c.input + c.output + c.cacheWrite5m + c.cacheWrite1h + c.cacheRead;

/** The receipt lines for one agent (or the total), skipping empty ones except input/output. */
export function costRows(c: CostLine): { key: string; label: string; tokens: string; usd: number }[] {
  const rows = [
    { key: "input", label: "Input", n: c.input, usd: c.usd.input, keep: true },
    { key: "output", label: "Output", n: c.output, usd: c.usd.output, keep: true },
    { key: "cw1h", label: "Cache write · 1 h", n: c.cacheWrite1h, usd: c.usd.cacheWrite1h, keep: false },
    { key: "cw5m", label: "Cache write · 5 min", n: c.cacheWrite5m, usd: c.usd.cacheWrite5m, keep: false },
    { key: "cr", label: "Cache read", n: c.cacheRead, usd: c.usd.cacheRead, keep: true },
    { key: "ws", label: "Web searches", n: c.webSearches, usd: c.usd.webSearches, keep: false },
  ];
  return rows.filter((r) => r.keep || r.n > 0).map((r) => ({
    key: r.key, label: r.label, usd: r.usd,
    tokens: r.key === "ws" ? String(r.n) : formatTokens(r.n),
  }));
}

/** "Today, 5:46 pm · ran 2m 6s" / "Sat 27 Sep, 11:02 am · ran 40s" from the task's first
 *  prompt and last API call (chats show no dates, so the receipt says when it happened). */
export function taskSpan(startedAt?: string, lastAt?: string, now = Date.now()): string {
  const s = startedAt ? Date.parse(startedAt) : NaN;
  if (!Number.isFinite(s)) return "";
  const d = new Date(s), today = new Date(now);
  const dayMs = 86_400_000, startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const daysAgo = Math.round((startOf(today) - startOf(d)) / dayMs);
  const day = daysAgo === 0 ? "Today" : daysAgo === 1 ? "Yesterday"
    : d.toLocaleDateString("en-SG", { weekday: "short", day: "numeric", month: "short", ...(d.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {}) }).replace(",", "");
  const when = `${day}, ${d.toLocaleTimeString("en-SG", { hour: "numeric", minute: "2-digit" }).toLowerCase()}`;
  const e = lastAt ? Date.parse(lastAt) : NaN;
  if (!Number.isFinite(e) || e <= s) return when;
  const sec = Math.round((e - s) / 1000);
  const ran = sec < 60 ? `${sec}s` : sec < 3600 ? `${Math.floor(sec / 60)}m ${sec % 60}s` : `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  return `${when} · ran ${ran}`;
}
